import { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { createPortal } from 'react-dom'
import Transition from '../../../transition'
import { fadeIn } from './transitions'
import WindowListener from '../../../window-listener'
import useScrollBlock from '../../../../hooks/use-scroll-block'

export default function BaseOverlay({
  children,
  className,
  onDismiss,
  show,
  transitions,
  backdrop = true,
  dismissOnEscape = false,
  dismissOnOutsideClick = true,
}) {
  const [modalElement] = useState(document.createElement('div'))
  const contentTransitions = transitions || fadeIn
  const [blockScroll, allowScroll] = useScrollBlock()

  const handleEscapeEvent = useCallback(
    event => event.keyCode === 27 && onDismiss(),
    [onDismiss]
  )

  useEffect(() => {
    document.body.appendChild(modalElement)
    return () => {
      document.body.removeChild(modalElement)
    }
  }, [modalElement])

  useEffect(() => {
    show ? blockScroll() : allowScroll()
    return () => {
      allowScroll()
    }
  }, [allowScroll, blockScroll, show])

  return (
    modalElement &&
    createPortal(
      <WindowListener
        onKeydown={event =>
          show && dismissOnEscape && handleEscapeEvent(event)
        }>
        <Transition show={show}>
          <div className="tw-text-base" data-testid="base-overlay">
            {backdrop && (
              <Transition
                enter="tw-transition tw-ease-in-out tw-duration-300"
                enterFrom="tw-bg-opacity-0"
                enterTo="tw-bg-opacity-50"
                leave="tw-transition tw-ease-in-out tw-duration-300"
                leaveFrom="tw-bg-opacity-50"
                leaveTo="tw-bg-opacity-0">
                <button
                  data-testid="base-overlay-backdrop"
                  onClick={dismissOnOutsideClick ? onDismiss : null}
                  tabIndex={-1}
                  className="tw-fixed tw-z-backdrop tw-border-none tw-inset-0 tw-bg-system-grey-600 tw-bg-opacity-50 tw-h-full tw-w-full tw-cursor-default"
                />
              </Transition>
            )}
            <Transition
              enter={contentTransitions.enter}
              enterFrom={contentTransitions.enterFrom}
              enterTo={contentTransitions.enterTo}
              leave={contentTransitions.leave}
              leaveFrom={contentTransitions.leaveFrom}
              leaveTo={contentTransitions.leaveTo}>
              <div data-testid="base-overlay-content" className={className}>
                {show && children}
              </div>
            </Transition>
          </div>
        </Transition>
      </WindowListener>,
      modalElement
    )
  )
}

BaseOverlay.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  onDismiss: PropTypes.func.isRequired,
  show: PropTypes.bool,
  transitions: PropTypes.shape({
    enter: PropTypes.string,
    enterFrom: PropTypes.string,
    enterTo: PropTypes.string,
    leave: PropTypes.string,
    leaveFrom: PropTypes.string,
    leaveTo: PropTypes.string,
  }),
  dismissOnEscape: PropTypes.bool,
}
